import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Navigate, Outlet, RouterProvider, createBrowserRouter } from "react-router-dom";
import { AppShell } from "@/app/AppShell";
import { APP_ROUTES } from "@/app/routes";
import { LegacyDetailRedirect } from "@/app/LegacyDetailRedirect";
import "./styles/fonts.css";
import "./styles/index.css";
import { AuthGate } from "@/app/AuthGate";
import { AnnouncerProvider } from "@/app/Announcer";
import { AgentChatProvider } from "@/features/agent/AgentChatProvider";
import { DebugTerminalWindow, DEBUG_TERMINAL_WINDOW_PATH } from "@/features/debug/DebugTerminalWindow";
import { I18nProvider } from "@/i18n/I18nProvider";
import { usingMockApi } from "@/lib/env";
import { ScopeAuthLifecycle } from "@/api/ScopeAuthLifecycle";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      /* 서버가 쿼리 비용을 강제하므로 클라이언트는 과도하게 재요청하지 않습니다. */
      refetchOnWindowFocus: false,
      retry: 1,
      gcTime: 5 * 60 * 1000,
    },
  },
});

/* 표시 언어는 인증 뒤에 정해집니다 — 운영자 기본값이 인증된 요청으로 옵니다.
   Router **안**에 두어야 `?lang=`을 읽고 라우트를 보존한 채 바꿀 수 있습니다.
   live region은 셸 밖에 하나만 둡니다(ADR 0025). */
function AppProviders() {
  return (
    <I18nProvider>
      <AnnouncerProvider>
        <AgentChatProvider>
          <Outlet />
        </AgentChatProvider>
      </AnnouncerProvider>
    </I18nProvider>
  );
}

/**
 * **data router를 씁니다**(ADR 0036). Debug Terminal 세션이 열려 있는 동안 라우트
 * 이탈을 확인 모달로 막으려면 `useBlocker`가 필요하고, 그것은 data router에서만
 * 동작합니다. 전역 클릭을 가로채 흉내 내면 `navigate()` 호출 경로와 뒤로가기를
 * 놓쳐, 작업 중이던 셸이 확인 없이 사라지는 경로가 남습니다.
 */
const router = createBrowserRouter([
  {
    element: <AppProviders />,
    children: [
      {
        element: <AppShell />,
        children: [
          /* 경로 집합의 단일 원천은 `@/app/routes`입니다. 라우터·좌측 nav·
             Command Palette가 같은 배열을 읽으므로 셋이 어긋날 수 없습니다. */
          ...APP_ROUTES.map((route) => ({ path: route.path, element: route.element })),
          /* 아래 셋은 라우팅이 아니라 리다이렉트라 레지스트리 밖에 둡니다. 옛 상세
             링크는 catch-all보다 **먼저** 잡아야 홈으로 튕기지 않습니다. (#111) */
          { path: "/workloads/:kind/:name", element: <LegacyDetailRedirect kind="workload" /> },
          { path: "/pods/:name", element: <LegacyDetailRedirect kind="pod" /> },
          { path: "*", element: <Navigate to="/" replace /> },
        ],
      },
      /* 별도 Window의 Debug Terminal (ADR 0036 결정 5). 셸 **밖**입니다 — 팝업에
         좌측 nav와 헤더를 싣지 않습니다. 작은 창에서 터미널이 차지할 자리를 뺏습니다. */
      { path: DEBUG_TERMINAL_WINDOW_PATH, element: <DebugTerminalWindow /> },
    ],
  },
]);

async function bootstrap() {
  // WSL→Windows npm 경계에서 인라인 환경 변수가 유실되어도 프로덕션은 mock 없이 빌드됩니다.
  // 프로덕션 mock은 명시적 true에서만 켜고, 개발 서버는 기존 기본값을 유지합니다.
  if (usingMockApi) {
    const { startMockApi } = await import("./mocks/browser");
    await startMockApi();
  }

  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ScopeAuthLifecycle>
          <AuthGate>
            <RouterProvider router={router} />
          </AuthGate>
        </ScopeAuthLifecycle>
      </QueryClientProvider>
    </StrictMode>,
  );
}

void bootstrap();
